import React, { useState, useRef, useEffect } from 'react';
import { DatePicker as AntDatePicker, ConfigProvider } from 'antd';
import type { DatePickerProps as AntDatePickerProps } from 'antd';
import type { RangePickerProps as AntRangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { CalendarDays, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { colors } from '../../theme/colors';
import { Tooltip, type TooltipPlacement, type TooltipVariant } from '../tool-tip';

/**
 * Auto-format date input to add dashes (YYYY-MM-DD format)
 * @param value - The raw input value
 * @returns Formatted date string with dashes
 */
const autoFormatDateInput = (value: string): string => {
    // Remove any non-digit and non-dash characters
    const digitsOnly = value.replace(/[^\d]/g, '');

    // Build the formatted string
    let formatted = '';
    for (let i = 0; i < digitsOnly.length && i < 8; i++) {
        if (i === 4 || i === 6) {
            formatted += '-';
        }
        formatted += digitsOnly[i];
    }

    return formatted;
};

/**
 * Validate a date string in YYYY-MM-DD format
 * @param dateStr - The date string to validate
 * @returns Object with isValid flag and optional error message
 */
const validateDateString = (dateStr: string): { isValid: boolean; error?: string } => {
    if (!dateStr || dateStr.length === 0) {
        return { isValid: true };
    }

    // Check if it's a complete date (YYYY-MM-DD = 10 chars)
    if (dateStr.length < 10) {
        return { isValid: true }; // Still typing, don't show error yet
    }

    // Parse the date parts
    const parts = dateStr.split('-');
    if (parts.length !== 3) {
        return { isValid: false, error: 'Invalid date format. Use YYYY-MM-DD' };
    }

    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);

    // Validate year (reasonable range)
    if (isNaN(year) || year < 1900 || year > 2100) {
        return { isValid: false, error: 'Invalid year. Enter a year between 1900 and 2100' };
    }

    // Validate month (1-12)
    if (isNaN(month) || month < 1 || month > 12) {
        return { isValid: false, error: 'Invalid month. Enter a month between 01 and 12' };
    }

    // Validate day based on month
    const daysInMonth = new Date(year, month, 0).getDate();
    if (isNaN(day) || day < 1 || day > daysInMonth) {
        return { isValid: false, error: `Invalid day. ${getMonthName(month)} has ${daysInMonth} days` };
    }

    // Final validation using dayjs
    const parsed = dayjs(dateStr, 'YYYY-MM-DD', true);
    if (!parsed.isValid()) {
        return { isValid: false, error: 'Invalid date format. Use YYYY-MM-DD' };
    }

    return { isValid: true };
};

/**
 * Get month name from month number
 */
const getMonthName = (month: number): string => {
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[month - 1] || 'Month';
};

const { RangePicker: AntRangePicker } = AntDatePicker;

// ============================================
// Types
// ============================================

export type DatePickerSize = 'small' | 'medium' | 'large';
export type DatePickerVariant = 'outlined' | 'filled' | 'borderless';
export type DatePickerMode = 'date' | 'week' | 'month' | 'quarter' | 'year';

export interface DatePickerProps
    extends Omit<AntDatePickerProps, 'size' | 'variant'> {
    /** Label for the date picker */
    label?: string;
    /** Whether the field is required */
    required?: boolean;
    /** Helper text below the input */
    helperText?: string;
    /** Error message */
    error?: string;
    /** Size of the date picker */
    size?: DatePickerSize;
    /** Variant style */
    variant?: DatePickerVariant;
    /** Whether to take full width */
    fullWidth?: boolean;
    /** Custom className */
    className?: string;
    /** Tooltip content for label */
    tooltip?: React.ReactNode;
    /** Tooltip placement */
    tooltipPlacement?: TooltipPlacement;
    /** Tooltip variant */
    tooltipVariant?: TooltipVariant;
}

export interface RangePickerProps
    extends Omit<AntRangePickerProps, 'size' | 'variant'> {
    /** Label for the range picker */
    label?: string;
    /** Whether the field is required */
    required?: boolean;
    /** Helper text below the input */
    helperText?: string;
    /** Error message */
    error?: string;
    /** Size of the range picker */
    size?: DatePickerSize;
    /** Variant style */
    variant?: DatePickerVariant;
    /** Whether to take full width */
    fullWidth?: boolean;
    /** Custom className */
    className?: string;
    /** Tooltip content for label */
    tooltip?: React.ReactNode;
    /** Tooltip placement */
    tooltipPlacement?: TooltipPlacement;
    /** Tooltip variant */
    tooltipVariant?: TooltipVariant;
}

// ============================================
// Size Configuration
// ============================================

const sizeConfig = {
    small: { height: 32, fontSize: '0.875rem', iconSize: 14 },
    medium: { height: 40, fontSize: '0.9375rem', iconSize: 16 },
    large: { height: 48, fontSize: '1rem', iconSize: 18 },
} as const;

const sizeMap: Record<DatePickerSize, 'small' | 'middle' | 'large'> = {
    small: 'small',
    medium: 'middle',
    large: 'large',
};

// ============================================
// Shared Components
// ============================================

interface LabelProps {
    children: React.ReactNode;
    required?: boolean;
    htmlFor?: string;
    tooltip?: React.ReactNode;
    tooltipPlacement?: TooltipPlacement;
    tooltipVariant?: TooltipVariant;
}

const Label: React.FC<LabelProps> = ({
    children,
    required,
    htmlFor,
    tooltip,
    tooltipPlacement = 'top',
    tooltipVariant = 'default',
}) => (
    <label
        htmlFor={htmlFor}
        className="inline-flex items-center gap-1 mb-1.5 text-sm font-medium"
        style={{ color: colors.textDefault }}
    >
        <span>{children}</span>
        {required && <span style={{ color: colors.error }}>*</span>}
        {tooltip && (
            <Tooltip
                title={tooltip}
                placement={tooltipPlacement}
                variant={tooltipVariant}
            >
                <span
                    className="inline-flex items-center cursor-help"
                    style={{ color: colors.textMuted }}
                >
                    <Info size={14} />
                </span>
            </Tooltip>
        )}
    </label>
);

interface HelperTextProps {
    children: React.ReactNode;
    isError?: boolean;
}

const HelperText: React.FC<HelperTextProps> = ({ children, isError }) => (
    <div
        className="mt-1 text-xs"
        style={{ color: isError ? colors.error : colors.textMuted }}
    >
        {children}
    </div>
);

// ============================================
// DatePicker Component
// ============================================

export const DatePicker: React.FC<DatePickerProps> = ({
    label,
    required = false,
    helperText,
    error,
    size = 'medium',
    variant = 'filled',
    fullWidth = true,
    className = '',
    style,
    onFocus,
    onBlur,
    onChange,
    value,
    defaultValue,
    tooltip,
    tooltipPlacement,
    tooltipVariant,
    suffixIcon,
    ...props
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const [internalError, setInternalError] = useState<string | null>(null);
    const hasValue = Boolean(value || defaultValue);
    const containerRef = useRef<HTMLDivElement>(null);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Auto-format date input and validate as user types
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const input = container.querySelector('input');
        if (!input) return;

        const handleInput = (e: Event) => {
            const target = e.target as HTMLInputElement;
            const cursorPosition = target.selectionStart || 0;
            const oldValue = target.value;
            const formatted = autoFormatDateInput(oldValue);

            if (formatted !== oldValue) {
                target.value = formatted;

                // Adjust cursor position to account for added dashes
                let newCursorPos = cursorPosition;
                const oldDigitsBeforeCursor = oldValue.slice(0, cursorPosition).replace(/[^\d]/g, '').length;
                let digitsCount = 0;
                for (let i = 0; i < formatted.length; i++) {
                    if (formatted[i] !== '-') {
                        digitsCount++;
                    }
                    if (digitsCount === oldDigitsBeforeCursor) {
                        newCursorPos = i + 1;
                        break;
                    }
                }
                target.setSelectionRange(newCursorPos, newCursorPos);
            }

            // Clear any pending validation timer
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            // Validate after a short delay (debounce)
            debounceTimerRef.current = setTimeout(() => {
                const validation = validateDateString(formatted);
                if (!validation.isValid) {
                    setInternalError(validation.error || 'Invalid date');
                } else {
                    setInternalError(null);
                    // If valid and complete (10 chars), sync with calendar by calling onChange
                    if (formatted.length === 10 && onChange) {
                        const parsedDate = dayjs(formatted, 'YYYY-MM-DD', true);
                        if (parsedDate.isValid()) {
                            onChange(parsedDate, formatted);
                        }
                    }
                }
            }, 300);
        };

        input.addEventListener('input', handleInput);
        return () => input.removeEventListener('input', handleInput);
    }, [onChange]);

    const handleFocus = (e: React.FocusEvent<HTMLElement>, ...args: any[]) => {
        setIsFocused(true);
        // @ts-ignore
        onFocus?.(e as any, ...args);
    };

    const handleBlur = (e: React.FocusEvent<HTMLElement>, ...args: any[]) => {
        setIsFocused(false);
        // @ts-ignore
        onBlur?.(e as any, ...args);
    };

    // Handle change from picker (calendar selection)
    const handleChange = (date: Dayjs | null, dateString: string | string[]) => {
        setInternalError(null); // Clear any error when selecting from calendar
        onChange?.(date, dateString);
    };

    // Combined error (external error takes precedence)
    const displayError = error || internalError;

    const getBorderColor = () => {
        if (displayError) return colors.error;
        if (isFocused) return colors.primary;
        if (hasValue) return colors.surfaceDark;
        return colors.surface;
    };

    const getBackgroundColor = () => {
        if (props.disabled) return colors.surfaceLighter;
        if (isFocused) return colors.white;
        return variant === 'filled' ? colors.surfaceLight : colors.white;
    };

    const config = sizeConfig[size];

    const defaultSuffixIcon = suffixIcon ?? (
        <CalendarDays
            size={config.iconSize}
            style={{ color: colors.textMuted }}
        />
    );

    return (
        <div className={cn(fullWidth && 'w-full', className)}>
            {label && (
                <Label
                    required={required}
                    tooltip={tooltip}
                    tooltipPlacement={tooltipPlacement}
                    tooltipVariant={tooltipVariant}
                >
                    {label}
                </Label>
            )}

            <ConfigProvider
                theme={{
                    token: {
                        colorPrimary: colors.primary,
                        colorError: colors.error,
                        colorTextPlaceholder: colors.textMuted,
                        colorText: colors.textDefault,
                        colorBorder: colors.surfaceSubtle,
                        borderRadius: 3,
                    },
                    components: {
                        DatePicker: {
                            controlHeight: config.height,
                            fontSize: parseInt(config.fontSize) || 14,
                            cellActiveWithRangeBg: colors.primaryExtraLight,
                            cellHoverBg: colors.surfaceLighter,
                            cellRangeBorderColor: colors.primary,
                        },
                    },
                }}
            >
                <div
                    ref={containerRef}
                    className={cn(
                        'flex items-center rounded-[3px] transition-all duration-150',
                        props.disabled && 'cursor-not-allowed opacity-60'
                    )}
                    style={{
                        height: config.height,
                        borderWidth: isFocused || displayError ? 2 : 1,
                        borderStyle: 'solid',
                        borderColor: variant === 'borderless' ? 'transparent' : getBorderColor(),
                        backgroundColor: variant === 'borderless' ? 'transparent' : getBackgroundColor(),
                    }}
                >
                    <AntDatePicker
                        value={value}
                        defaultValue={defaultValue}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        size={sizeMap[size]}
                        variant="borderless"
                        status={displayError ? 'error' : undefined}
                        className="w-full"
                        style={{
                            height: '100%',
                            fontSize: config.fontSize,
                            ...style,
                        }}
                        suffixIcon={defaultSuffixIcon}
                        {...props}
                    />
                </div>
            </ConfigProvider>

            {helperText && !displayError && <HelperText>{helperText}</HelperText>}
            {displayError && <HelperText isError>{displayError}</HelperText>}
        </div>
    );
};

// ============================================
// RangePicker Component
// ============================================

export const RangePicker: React.FC<RangePickerProps> = ({
    label,
    required = false,
    helperText,
    error,
    size = 'medium',
    variant = 'filled',
    fullWidth = true,
    className = '',
    style,
    onFocus,
    onBlur,
    value,
    defaultValue,
    tooltip,
    tooltipPlacement,
    tooltipVariant,
    suffixIcon,
    ...props
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const hasValue = Boolean(value || defaultValue);

    const handleFocus = (e: React.FocusEvent<HTMLElement>, ...args: any[]) => {
        setIsFocused(true);
        // @ts-ignore
        onFocus?.(e as any, ...args);
    };

    const handleBlur = (e: React.FocusEvent<HTMLElement>, ...args: any[]) => {
        setIsFocused(false);
        // @ts-ignore
        onBlur?.(e as any, ...args);
    };

    const getBorderColor = () => {
        if (error) return colors.error;
        if (isFocused) return colors.primary;
        if (hasValue) return colors.surfaceDark;
        return colors.surface;
    };

    const getBackgroundColor = () => {
        if (props.disabled) return colors.surfaceLighter;
        if (isFocused) return colors.white;
        return variant === 'filled' ? colors.surfaceLight : colors.white;
    };

    const config = sizeConfig[size];

    const defaultSuffixIcon = suffixIcon ?? (
        <CalendarDays
            size={config.iconSize}
            style={{ color: colors.textMuted }}
        />
    );

    return (
        <div className={cn(fullWidth && 'w-full', className)}>
            {label && (
                <Label
                    required={required}
                    tooltip={tooltip}
                    tooltipPlacement={tooltipPlacement}
                    tooltipVariant={tooltipVariant}
                >
                    {label}
                </Label>
            )}

            <ConfigProvider
                theme={{
                    token: {
                        colorPrimary: colors.primary,
                        colorError: colors.error,
                        colorTextPlaceholder: colors.textMuted,
                        colorText: colors.textDefault,
                        colorBorder: colors.surfaceSubtle,
                        borderRadius: 3,
                    },
                    components: {
                        DatePicker: {
                            controlHeight: config.height,
                            fontSize: parseInt(config.fontSize) || 14,
                            // Range selection colors
                            cellActiveWithRangeBg: colors.primaryExtraLight,
                            cellHoverWithRangeBg: colors.primaryExtraLight,
                            cellHoverBg: colors.surfaceLighter,
                            cellRangeBorderColor: colors.primary,
                            // Active cell styling
                            activeBorderColor: colors.primary,
                            hoverBorderColor: colors.primaryHover,
                        },
                    },
                }}
            >
                <div
                    className={cn(
                        'flex items-center rounded-[3px] transition-all duration-150',
                        props.disabled && 'cursor-not-allowed opacity-60'
                    )}
                    style={{
                        height: config.height,
                        borderWidth: isFocused || error ? 2 : 1,
                        borderStyle: 'solid',
                        borderColor: variant === 'borderless' ? 'transparent' : getBorderColor(),
                        backgroundColor: variant === 'borderless' ? 'transparent' : getBackgroundColor(),
                    }}
                >
                    <AntRangePicker
                        value={value}
                        defaultValue={defaultValue}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        size={sizeMap[size]}
                        variant="borderless"
                        status={error ? 'error' : undefined}
                        className="w-full"
                        style={{
                            height: '100%',
                            fontSize: config.fontSize,
                            ...style,
                        }}
                        suffixIcon={defaultSuffixIcon}
                        {...props}
                    />
                </div>
            </ConfigProvider>

            {helperText && !error && <HelperText>{helperText}</HelperText>}
            {error && <HelperText isError>{error}</HelperText>}
        </div>
    );
};

// ============================================
// Convenience Components
// ============================================

export const MonthPicker: React.FC<DatePickerProps> = (props) => (
    <DatePicker picker="month" {...props} />
);

export const YearPicker: React.FC<DatePickerProps> = (props) => (
    <DatePicker picker="year" {...props} />
);

export const WeekPicker: React.FC<DatePickerProps> = (props) => (
    <DatePicker picker="week" {...props} />
);

export const QuarterPicker: React.FC<DatePickerProps> = (props) => (
    <DatePicker picker="quarter" {...props} />
);

export const TimePicker: React.FC<DatePickerProps> = (props) => (
    <DatePicker picker="time" {...props} />
);

// ============================================
// Utility exports
// ============================================

export { dayjs };
export type { Dayjs };

export default DatePicker;
